import type { Action, CryptoCurrency, Transfer } from '@0xtorch/core'
import { z } from 'zod'
import { cardanoAddressSchema, isCardanoScriptPaymentAddress } from './address'
import { cardanoAda } from './asset'
import {
  type CardanoUtxos,
  cardanoTransactionSchema,
  cardanoUtxosSchema,
  cardanoWithdrawalSchema,
} from './schemas'
import { createCardanoTxSourceId } from './source'

/**
 * eUTxO の自己保有純増減から既存形式の汎用 Action と手数料を生成する。
 * @param params 検証対象 Tx/UTxO、所有 Stake / Enterprise 集合、引出し、実額に基づく保証金増減、Native Asset 定義
 * @returns お釣り・内部移動・引出し・保証金の二重計上を除外した Action
 */
export const analyzeCardanoTransaction = (params: {
  transaction: unknown
  utxos: unknown
  stakeAddresses: ReadonlySet<string>
  enterpriseAddresses?: ReadonlySet<string>
  withdrawals: unknown
  depositDeltas: ReadonlyMap<string, bigint>
  assets: ReadonlyMap<string, { asset: CryptoCurrency; decimals: number }>
}): readonly Action[] => {
  const tx = cardanoTransactionSchema.parse(params.transaction)
  const utxos = cardanoUtxosSchema.parse(params.utxos)
  if (tx.hash !== utxos.hash)
    throw new Error('Cardano transaction hash mismatch')
  const withdrawals = z.array(cardanoWithdrawalSchema).parse(params.withdrawals)
  const delta = new Map<string, bigint>()
  const owners = new Map<string, string>()
  const inputOwners = new Set<string>()
  const ownedAddresses = new Set<string>()
  let foreignInput = false
  const inputs = utxos.inputs.filter(
    (input) => !input.reference && input.collateral === !tx.valid_contract,
  )
  const outputs = utxos.outputs.filter(
    (output) => output.collateral === !tx.valid_contract,
  )
  for (const [rows, sign] of [
    [inputs, -1n],
    [outputs, 1n],
  ] as const) {
    for (const row of rows) {
      const parsed = cardanoAddressSchema.safeParse(row.address)
      const owner =
        parsed.success &&
        parsed.data.type === 'base' &&
        params.stakeAddresses.has(parsed.data.stakeAddress)
          ? parsed.data.stakeAddress
          : parsed.success &&
              parsed.data.type === 'enterprise' &&
              params.enterpriseAddresses?.has(parsed.data.address)
            ? parsed.data.address
            : undefined
      if (sign === -1n) {
        if (owner === undefined) foreignInput = true
        else inputOwners.add(owner)
      }
      if (owner === undefined) continue
      ownedAddresses.add(row.address)
      for (const amount of row.amount) {
        delta.set(
          amount.unit,
          (delta.get(amount.unit) ?? 0n) + sign * BigInt(amount.quantity),
        )
        owners.set(amount.unit, owner)
      }
    }
  }
  if (tx.valid_contract) {
    for (const withdrawal of withdrawals) {
      if (!params.stakeAddresses.has(withdrawal.address)) continue
      delta.set(
        'lovelace',
        (delta.get('lovelace') ?? 0n) - BigInt(withdrawal.amount),
      )
    }
    for (const [stake, amount] of params.depositDeltas) {
      if (params.stakeAddresses.has(stake))
        delta.set('lovelace', (delta.get('lovelace') ?? 0n) + amount)
    }
  }
  if (
    !tx.valid_contract &&
    [...delta].some(([unit, amount]) => unit !== 'lovelace' && amount !== 0n)
  ) {
    throw new Error('Cardano collateral native asset balance is not conserved')
  }
  // ScriptがADAを供給せず、通常入力の所有Stakeが一意なら、そのWalletからfeeを分離する。
  // ScriptもADAを供給する場合や他人の通常入力がある場合は負担者を推測しない。
  const scriptsDoNotFundAda =
    tx.valid_contract &&
    tx.deposit === '0' &&
    tx.withdrawal_count === 0 &&
    withdrawals.length === 0 &&
    tx.stake_cert_count === 0 &&
    tx.pool_update_count === 0 &&
    tx.pool_retire_count === 0 &&
    [...params.depositDeltas.values()].every((amount) => amount === 0n) &&
    (delta.get('lovelace') ?? 0n) <= -BigInt(tx.fees) &&
    hasNonFundingScriptInputs({ inputs, outputs, ownedAddresses })
  const feeOwner =
    (!foreignInput || scriptsDoNotFundAda) && inputOwners.size === 1
      ? inputOwners.values().next().value
      : undefined
  const fee =
    feeOwner === undefined
      ? 0n
      : tx.valid_contract
        ? BigInt(tx.fees)
        : -(delta.get('lovelace') ?? 0n)
  if (fee < 0n) throw new Error('Invalid Cardano collateral balance')
  delta.set('lovelace', (delta.get('lovelace') ?? 0n) + fee)
  // ADAだけの通常送金では、UTxOから相手方を一意に追跡できる場合に限って補完する。
  const simpleAda =
    tx.valid_contract &&
    tx.deposit === '0' &&
    tx.stake_cert_count === 0 &&
    tx.pool_update_count === 0 &&
    tx.pool_retire_count === 0 &&
    withdrawals.length === 0 &&
    [...params.depositDeltas.values()].every((amount) => amount === 0n) &&
    [...inputs, ...outputs].every((row) =>
      row.amount.every((amount) => amount.unit === 'lovelace'),
    )
  const senders = new Set(inputs.map((row) => row.address))
  const recipients = new Set<string>()
  let externalOutputAmount = 0n
  for (const output of outputs) {
    if (ownedAddresses.has(output.address)) continue
    for (const amount of output.amount) {
      if (amount.unit !== 'lovelace' || BigInt(amount.quantity) === 0n) continue
      recipients.add(output.address)
      externalOutputAmount += BigInt(amount.quantity)
    }
  }
  const transfers: Transfer[] = []
  for (const [unit, amount] of [...delta.entries()].sort(([left], [right]) =>
    left.localeCompare(right),
  )) {
    if (amount === 0n) continue
    const definition =
      unit === 'lovelace'
        ? { asset: cardanoAda, decimals: 6 }
        : params.assets.get(unit)
    if (definition === undefined)
      throw new Error(`Missing Cardano native asset: ${unit}`)
    const owner = owners.get(unit)
    const counterparty =
      simpleAda && unit === 'lovelace'
        ? amount > 0n && inputOwners.size === 0 && senders.size === 1
          ? senders.values().next().value
          : amount < 0n &&
              !foreignInput &&
              inputOwners.size === 1 &&
              recipients.size === 1 &&
              externalOutputAmount === -amount
            ? recipients.values().next().value
            : undefined
        : undefined
    transfers.push({
      direction: amount > 0n ? 'in' : 'out',
      ...(amount > 0n
        ? { from: counterparty, to: owner }
        : { from: owner, to: counterparty }),
      asset: definition.asset,
      amount: {
        value: amount > 0n ? amount : -amount,
        decimals: definition.decimals,
      },
    })
  }
  const source = createCardanoTxSourceId({ txHash: tx.hash })
  const timestamp = tx.block_time * 1000
  const actions: Action[] = []
  const incoming = transfers.filter((transfer) => transfer.direction === 'in')
  const outgoing = transfers.filter((transfer) => transfer.direction === 'out')
  if (incoming.length === 1 && outgoing.length === 1) {
    actions.push({
      source,
      timestamp,
      order: 0,
      evidence: 'none',
      type: 'trade',
      transfers,
    })
  } else {
    for (const transfer of transfers) {
      actions.push({
        source,
        timestamp,
        order: actions.length,
        evidence: 'none',
        type: transfer.direction === 'in' ? 'gift' : 'transfer',
        transfers: [transfer],
      })
    }
  }
  if (fee > 0n) {
    actions.push({
      source,
      timestamp,
      order: Math.max(1, actions.length),
      evidence: 'system-rule',
      type: 'transaction-fee',
      transfers: [
        {
          direction: 'out',
          from: feeOwner,
          asset: cardanoAda,
          amount: { value: fee, decimals: 6 },
        },
      ],
    })
  }
  return actions
}

/**
 * 未所有入力が対応Scriptのみで、各ScriptアドレスのADA残高が減っていないことを確認する。
 * 返却・状態継続でScriptのADAが維持されるTxでは、Scriptをfeeの資金供給者にしない。
 * @param params 担保・Referenceを除外した入出力と所有Payment Address集合
 * @returns 全ての未所有入力ScriptでADA出力合計が入力合計以上ならtrue
 */
const hasNonFundingScriptInputs = (params: {
  inputs: CardanoUtxos['inputs']
  outputs: CardanoUtxos['outputs']
  ownedAddresses: ReadonlySet<string>
}): boolean => {
  const balances = new Map<string, bigint>()
  for (const input of params.inputs) {
    if (params.ownedAddresses.has(input.address)) continue
    if (!isCardanoScriptPaymentAddress({ address: input.address })) return false
    for (const amount of input.amount) {
      if (amount.unit === 'lovelace')
        balances.set(
          input.address,
          (balances.get(input.address) ?? 0n) - BigInt(amount.quantity),
        )
    }
  }
  if (balances.size === 0) return false
  for (const output of params.outputs) {
    if (!balances.has(output.address)) continue
    for (const amount of output.amount) {
      if (amount.unit === 'lovelace')
        balances.set(
          output.address,
          (balances.get(output.address) ?? 0n) + BigInt(amount.quantity),
        )
    }
  }
  return [...balances.values()].every((amount) => amount >= 0n)
}
